import {
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Event as NavigationEvent, Router } from '@angular/router';
import { filter, Subscription, tap } from 'rxjs';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { UserService } from '../../services/infra/user/user.service';
import { AccountService } from 'src/app/services/infra/account/account.service';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccessService } from 'src/app/services/infra/access/access.service';
import { MenuComponent } from '../../components/menu/menu.component';
import { NavigationConfigService } from 'src/app/services/navigation/navigation-config.service';
import { IAppPage } from 'src/app/models/navigation/navigation.interface';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let pendo: any;

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent implements OnInit, OnDestroy {
  @ViewChild('menuComponent') menuComponent: MenuComponent;

  public appPages = signal<IAppPage[]>([]);
  public supportSectionRefPages = signal<IAppPage[]>([]);
  public activePageTitleLabelPath = signal('');
  public activeTabTitleLabelPath = signal<string | undefined>(undefined);
  public titleIconPath = signal<string | undefined>(undefined);
  public isMenuOpen = signal(true);
  public menuExpanded = signal(true);
  public menuExtended = signal(true);
  public baseUrl: string;
  public activeMenuItemIndex = signal(0);

  private readonly _userSubscription: Subscription;

  private readonly _accountService = inject(AccountService);
  private readonly _accessService = inject(AccessService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _featureFlagsService = inject(FeatureFlagsService);

  constructor(
    private readonly _router: Router,
    public logger: LoggerService,
    private readonly _userService: UserService,
    private readonly _navigationConfigService: NavigationConfigService
  ) {}

  ngOnInit(): void {
    this.baseUrl = location.origin;
    this._initAppPages();

    this._accountService
      .getAccountCategory$()
      .pipe(
        filter((accountCategory) => accountCategory !== null),
        tap((accountCategory) => {
          if (this._accessService.renderAPITabAndDocsButton(accountCategory)) {
            this._initSupportSectionRefPages();
          }
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();

    this._setActiveAppPage(this._router.url);
    this._router.events.subscribe((event: NavigationEvent) => {
      if (event instanceof NavigationEnd) {
        this._setActiveAppPage(event.url);
      }
    });

    const loggedInUser = this._userService.loggedInUser;
    pendo.initialize({
      visitor: {
        id: loggedInUser.email,
        email: loggedInUser.email,
      },
      account: {
        id: loggedInUser['org_id'],
        name: this._userService.accountName,
      },
    });
  }

  ngOnDestroy() {
    this._userSubscription?.unsubscribe();
  }

  public onMenuStateChange = (menuState: boolean) => this.isMenuOpen.set(menuState);

  public onMenuStateChanged(state: { isExpanded: boolean; isLocked: boolean }) {
    this.menuExpanded.set(state.isExpanded);
    this.menuExtended.set(state.isLocked); // Using isLocked as isExtended for backward compatibility
  }

  public onExpansionChanged(): void {
    this._updateSelectionVisibility();
  }

  public isIssuesPage(): boolean {
    return this.activePageTitleLabelPath() === 'menu.issuesPage';
  }

  public shouldShowPreviewBadge(): boolean {
    return (
      this.isIssuesPage() && !this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.ReactHideIssuesPreviewBadge)
    );
  }

  public shouldShowPageTitle(): boolean {
    const isNewIntegrationLayoutEnabled = this._featureFlagsService.isFeatureEnabled(
      FeatureFlagTypes.EnableNewIntegrationPageLayout
    );
    const isIntegrationsPage = this.activePageTitleLabelPath() === 'menu.integrationsPage';

    // Hide title if new integration layout is enabled AND we're on the integrations page
    if (isNewIntegrationLayoutEnabled && isIntegrationsPage) {
      return false;
    }
    return true;
  }

  public clearAllSelections() {
    this._clearAllSelections();
  }

  public toggleParentExpansion(parentPage: IAppPage) {
    parentPage.isExpanded = !parentPage.isExpanded;
  }

  private _setActiveAppPage(url: string): void {
    const [baseUrl] = url.split('?');
    let activePageFound = false;
    this._clearAllSelections();
    this.activeTabTitleLabelPath.set(undefined);

    // Check main app pages first
    for (let i = 0; i < this.appPages().length; i++) {
      const appPage = this.appPages()[i];

      // Handle parent pages with children
      if (Array.isArray(appPage.children) && appPage.children.length > 0) {
        const activeChild = appPage.children.find(
          (child) =>
            this._isUrlContainsNavigationPage(baseUrl, child.navigationTo, child.navigationQuery) && !child.newTab
        );

        if (activeChild) {
          activeChild.isActive = true;
          activeChild.selected = true;
          appPage.isExpanded = true;
          appPage.isActive = true;
          appPage.selected = false;

          this.activePageTitleLabelPath.set(activeChild.displayTitleLabelPath);
          this.titleIconPath.set(activeChild.titleIconPath);
          this.activeMenuItemIndex.set(i);

          activePageFound = true;
          break;
        }

        appPage.isActive = false;
        appPage.selected = false;
      }

      // Handle standalone pages (no children)
      else if (
        this._isUrlContainsNavigationPage(baseUrl, appPage.navigationTo, appPage.navigationQuery) &&
        !appPage.newTab
      ) {
        appPage.isActive = true;
        appPage.selected = true;

        this.activePageTitleLabelPath.set(appPage.displayTitleLabelPath);
        this.titleIconPath.set(appPage.titleIconPath);
        this.activeMenuItemIndex.set(i);

        activePageFound = true;
        break;
      }
    }

    // If no main page was found, check support section pages
    if (!activePageFound) {
      for (let i = 0; i < this.supportSectionRefPages().length; i++) {
        const supportPage = this.supportSectionRefPages()[i];

        // Handle parent support pages with children
        if (Array.isArray(supportPage.children) && supportPage.children.length > 0) {
          const activeChild = supportPage.children.find(
            (child) =>
              this._isUrlContainsNavigationPage(baseUrl, child.navigationTo, child.navigationQuery) && !child.newTab
          );

          if (activeChild) {
            activeChild.isActive = true;
            activeChild.selected = true;
            supportPage.isExpanded = true;
            supportPage.isActive = true;
            supportPage.selected = false;

            this.activePageTitleLabelPath.set(activeChild.displayTitleLabelPath);
            this.titleIconPath.set(activeChild.titleIconPath);

            activePageFound = true;
            break;
          }

          supportPage.isActive = false;
          supportPage.selected = false;
        }

        // Handle standalone support pages (no children)
        else if (
          this._isUrlContainsNavigationPage(baseUrl, supportPage.navigationTo, supportPage.navigationQuery) &&
          !supportPage.newTab
        ) {
          supportPage.isActive = true;
          supportPage.selected = true;

          this.activePageTitleLabelPath.set(supportPage.displayTitleLabelPath);
          this.titleIconPath.set(supportPage.titleIconPath);

          activePageFound = true;
          break;
        }
      }
    }

    // Force change detection by updating the signal
    this.appPages.set([...this.appPages()]);
    this.supportSectionRefPages.set([...this.supportSectionRefPages()]);

    // Notify the menu component to update its indicator
    this.menuComponent?.updateSelection();
  }

  private _clearAllSelections(): void {
    // Reset all parent and child menu states to prevent stale selections
    this.appPages()?.forEach((appPage) => {
      appPage.isActive = false;
      appPage.selected = false;

      appPage.children?.forEach((child) => {
        child.isActive = false;
        child.selected = false;
      });
    });

    // Also clear support section pages
    this.supportSectionRefPages()?.forEach((supportPage) => {
      supportPage.isActive = false;
      supportPage.selected = false;

      supportPage.children?.forEach((child) => {
        child.isActive = false;
        child.selected = false;
      });
    });
  }

  private _updateSelectionVisibility(): void {
    this.appPages()?.forEach((appPage) => {
      if (!Array.isArray(appPage.children)) return;

      appPage.children?.forEach((child) => {
        child.selected = !!child.isActive && !!appPage.isExpanded;
      });
    });

    this.supportSectionRefPages()?.forEach((supportPage) => {
      if (!Array.isArray(supportPage.children)) return;

      supportPage.children?.forEach((child) => {
        child.selected = !!child.isActive && !!supportPage.isExpanded;
      });
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _isUrlContainsNavigationPage(url: string, navigateToString: string, navigationQuery?: any) {
    // Handle absolute paths like /app/violations
    if (navigateToString.startsWith('/app')) {
      // Special case for the overview page (/app) - exact match only
      if (navigateToString === '/app') {
        return url === '/app' || url.startsWith('/app?');
      }

      // Check if the base path matches
      const [baseUrl, queryString] = url.split('?');
      const basePathMatches = baseUrl === navigateToString || baseUrl.startsWith(navigateToString + '/');

      if (!basePathMatches) {
        return false;
      }

      // Legacy support: If this navigation item has a tab parameter (for backward compatibility with old ai-detection route)
      if (navigationQuery && navigationQuery.tab && navigateToString === '/app/ai-detection') {
        // URL must have query parameters and the tab must match exactly
        if (!queryString) {
          return false; // No query params in URL, but navigation expects tab
        }

        const urlParams = new URLSearchParams(queryString);
        const urlTab = urlParams.get('tab');
        return urlTab === navigationQuery.tab;
      }

      // For new individual routes (ai-findings, ai-activity, etc.), exact path matching
      if (navigateToString.match(/\/app\/ai-(findings|activity|prevention|data-insights|in-line-control)$/)) {
        return baseUrl === navigateToString;
      }

      // For legacy ai-detection path, if there's no tab in navigationQuery, only match if URL has no tab param
      if (navigateToString === '/app/ai-detection') {
        return !queryString || !new URLSearchParams(queryString).get('tab');
      }

      // For other paths, match if base path matches
      return true;
    }

    // Handle relative paths (fallback for existing logic)
    const splitUrl = url.split('/');
    if (splitUrl.length > 2 && splitUrl.includes(navigateToString)) {
      return true;
    }
    return splitUrl.length == 2 && url.includes(navigateToString);
  }

  private _initAppPages() {
    this.appPages.set(this._navigationConfigService.getAppPages());
  }

  private _initSupportSectionRefPages() {
    this.supportSectionRefPages.set(this._navigationConfigService.getSupportSectionPages());
  }
}
